<template>
  <div class="chatbot-upload-page">
    <div class="upload-container">
      <!-- Header -->
      <div class="page-header">
        <div class="breadcrumb-nav">
          <button @click="goBack" class="breadcrumb-link back-btn">
            <i class="las la-arrow-left"></i>
            Back
          </button>
        </div>
        <h1 class="page-title">{{ selectedFile ? 'Re-upload File' : 'Upload New File' }}</h1>
        <p class="page-subtitle" v-if="selectedFile">
          Re-uploading: <strong>{{ selectedFile.file_name || selectedFile.name }}</strong>
        </p>
        <p class="page-subtitle" v-else>Upload a document<span v-if="selectedCandidate && selectedCandidate.name"> for {{ selectedCandidate.name }}</span></p>
      </div>

      <!-- File Upload Component -->
      <FileUpload 
        @file-selected="handleFileUpload"
        @batch-files-selected="handleBatchFileUpload"
        @cancel="goBack"
        @pre-selected-file-cleared="clearSelectedFile"
        :scan-progress="uploadProgress"
        :pre-selected-file="selectedFile"
      />
      
      <!-- Error Display -->
      <div v-if="error" class="error-message">
        <i class="las la-exclamation-circle"></i>
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import FileUpload from './components/FileUpload.vue'
import ChatbotService from "@/services/chatbot.service";

export default {
  name: 'ChatbotUpload',
  components: {
    FileUpload
  },
  props: {
    preSelectedFile: {
      type: Object,
      default: null
    },
    candidateId: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      uploading: false,
      error: null,
      selectedFile: null,
      selectedCandidate: null,
      uploadProgress: {
        show: false,
        percentage: 0,
        status: 'processing',
        currentPage: 0,
        totalPages: 0,
        taskType: 'upload',
        message: '',
        file_name: '',
        upload_id: '',
        batchFiles: [] // Array to store individual file progress
      },
    }
  },
  mounted() {
    // Fetch candidate information
    this.fetchCandidateInfo();
    
    // Check for file in query parameters (from scan again button)
    if (this.$route.query.file) {
      try {
        const fileFromQuery = JSON.parse(this.$route.query.file);
        this.selectedFile = fileFromQuery;
      } catch (error) {
        console.error('Error parsing file from query:', error);
      }
    }
    
    // If a file is pre-selected via props, use it
    if (this.preSelectedFile) {
      this.selectedFile = this.preSelectedFile;
    }
  },
  methods: {
    goBack() {
      if (this.candidateId) {
        // Navigate back to the candidate sessions page
        this.$router.push({ 
          name: 'chatbot-candidate-sessions',
          params: { candidateId: this.candidateId }
        });
      } else {
        // Fall back to general chatbot page
        this.$router.push({ name: 'chatbot' });
      }
    },
    
    clearSelectedFile() {
      this.selectedFile = null;
      // Clear the query parameter
      this.$router.replace({ query: {} });
    },
    
    async fetchCandidateInfo() {
      if (!this.candidateId) {
        return; // No candidate ID, skip fetching
      }
      
      try {
        const candidate = await ChatbotService.getFinancingCandidate(this.candidateId);
        this.selectedCandidate = candidate;
      } catch (error) {
        console.error('Error fetching candidate info:', error);
        this.error = 'Failed to load candidate information.';
      }
    },

    // Unified upload method that handles both new file uploads and pre-selected files
    async handleFileUpload(file, fileType, password = null) {
      this.uploading = true;
      this.error = null;
      
      try {
        let fileToUpload = file;
        
        // If we have a pre-selected file with file_url, create a proper File object
        if (this.selectedFile && this.selectedFile.file_url && !(file instanceof File)) {
          try {
            const response = await fetch(this.selectedFile.file_url);
            const blob = await response.blob();
            const correctFileName = this.selectedFile.file_name || this.selectedFile.name || 'document.pdf';
            const correctFileType = this.selectedFile.document_type || 'application/pdf';
            
            fileToUpload = new File([blob], correctFileName, {
              type: correctFileType
            });
          } catch (fetchError) {
            console.error('Error fetching file from URL:', fetchError);
            fileToUpload = file;
          }
        }
        
        // Upload the file
        const data = await this.uploadFile(fileToUpload, fileType, password);
        console.log('Upload response:', data);
        
        if (data.status === 'processing' || data.status==='extracting' && data.upload_id) {
          // Track with simple task tracker (polling first)
          this.$taskTracker.trackTask(data.upload_id, {
            onProgress: (progress) => {
              console.log('Upload progress:', progress);
              this.uploadProgress.show = true
              this.uploadProgress.percentage = progress.progress || 0
              this.uploadProgress.message = progress.message || 'Processing...'
              this.uploadProgress.currentPage = progress.current_page || 0
              this.uploadProgress.totalPages = progress.total_pages || 0
              this.uploadProgress.status = progress.status || 'processing'
              if (progress.status === 'extracting'){
                this.uploadProgress.message = 'Extracting transactions...'
              }
            },
            onSuccess: (result) => {
              this.uploadProgress.show = false
              this.handleUploadSuccess(result, fileToUpload)
            },
            onError: (error) => {
              this.uploadProgress.show = false
              this.handleUploadError(error)
            }
          });
        } else if (data.status === 'completed') {
          this.handleUploadSuccess(data, fileToUpload);
        }
      } catch (err) {
        this.handleUploadError(err);
      }
      
      this.uploading = false;
    },

    // Handle batch file upload
    async handleBatchFileUpload(files, fileType, password = null) {
      this.uploading = true;
      this.error = null;
      
      try {
        console.log('Batch uploading files:', files);
        
        // Upload files in batch
        const data = await this.batchUploadFiles(files, fileType, password);
        console.log('Batch upload response:', data);
        
        if (data.batch_id) {
          // Track batch upload with polling
          this.trackBatchUpload(data.batch_id, files);
        } else {
          this.handleUploadError(new Error('No batch ID received from server'));
        }
      } catch (err) {
        this.handleUploadError(err);
      }
      
      this.uploading = false;
    },


    // Track batch upload progress
    async trackBatchUpload(batchId, files) {
      this.uploadProgress.show = true;
      this.uploadProgress.percentage = 0;
      this.uploadProgress.message = 'Starting batch upload...';
      this.uploadProgress.taskType = 'batch-upload';
      
      // Initialize batch files array with the selected files
      this.uploadProgress.batchFiles = files.map((file, index) => ({
        file_name: file.name,
        file_index: index,
        status: 'pending',
        progress: 0,
        current_page: 0,
        total_pages: 0,
        message: 'Waiting to start...'
      }));
      
      const pollInterval = setInterval(async () => {
        try {
          const status = await ChatbotService.getBatchStatus(batchId);
          console.log('Batch status:', status);
          
          if (status.status === 'completed') {
            clearInterval(pollInterval);
            this.uploadProgress.show = false;
            this.handleBatchUploadSuccess(status, files);
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            this.uploadProgress.show = false;
            this.handleUploadError(new Error(status.error || 'Batch upload failed'));
          } else if (status.status === 'processing') {
            // Update overall progress
            this.uploadProgress.percentage = status.progress_percentage || 0;
            
            // Update individual file progress
            if (status.uploads && status.uploads.length > 0) {
              this.uploadProgress.batchFiles = this.uploadProgress.batchFiles.map(batchFile => {
                const uploadData = status.uploads.find(upload => upload.file_index === batchFile.file_index);
                if (uploadData) {
                  let message = uploadData.message || `Status: ${uploadData.status}`;
                  
                  // Handle extracting status
                  if (uploadData.status === 'extracting') {
                    message = 'Extracting transactions...';
                  }
                  
                  return {
                    ...batchFile,
                    status: uploadData.status,
                    progress: uploadData.progress || 0,
                    current_page: uploadData.current_page || 0,
                    total_pages: uploadData.total_pages || 0,
                    message: message
                  };
                }
                return batchFile;
              });
            }
            
            // Create detailed progress message
            const completed = status.completed_uploads || 0;
            const failed = status.failed_uploads || 0;
            const processing = status.processing_uploads || 0;
            const total = status.total_files || files.length;
            
            // Find currently processing file
            const currentUpload = status.uploads && status.uploads.find(upload => upload.status === 'processing' || upload.status === 'extracting');
            if (currentUpload) {
              if (currentUpload.status === 'extracting') {
                this.uploadProgress.message = `Extracting transactions from "${currentUpload.file_name}"... (${completed}/${total} completed)`;
              } else {
                this.uploadProgress.message = `Processing "${currentUpload.file_name}"... (${completed}/${total} completed)`;
              }
              this.uploadProgress.currentPage = currentUpload.current_page || 0;
              this.uploadProgress.totalPages = currentUpload.total_pages || 0;
            } else {
              this.uploadProgress.message = `Processing files... (${completed}/${total} completed)`;
            }
            
            // Show failed files if any
            if (failed > 0) {
              this.uploadProgress.message += `, ${failed} failed`;
            }
            
            // Check if all files are completed but batch status is still processing
            if (completed === total && status.status === 'processing') {
              this.uploadProgress.message = 'Finalizing batch upload...';
            }
          }
        } catch (error) {
          console.error('Error polling batch status:', error);
          clearInterval(pollInterval);
          this.uploadProgress.show = false;
          this.handleUploadError(error);
        }
      }, 5000); // Poll every 5 seconds
    },

    async uploadFile(file, fileType, password = null) {
      if (!this.candidateId) {
        throw new Error('Candidate ID is required for file uploads');
      }
      return ChatbotService.uploadFile(this.candidateId, file, fileType, password);
    },


    async batchUploadFiles(files, fileType, password = null) {
      if (!this.candidateId) {
        throw new Error('Candidate ID is required for batch uploads');
      }
      return ChatbotService.batchUploadFiles(this.candidateId, files, fileType, password);
    },


    handleUploadSuccess(data, file) {
      this.hideProgressBar();
      
      if (this.candidateId) {
        // Navigate to the candidate files page
        this.$router.push({ 
          name: 'chatbot-candidate-files',
          params: { candidateId: this.candidateId }
        });
      } else {
        // Fall back to general chatbot page
        this.$router.push({ name: 'chatbot' });
      }
    },

    handleBatchUploadSuccess(data, files) {
      this.hideProgressBar();
      
      // Create detailed success message based on actual response
      const completed = data.completed_uploads || 0;
      const failed = data.failed_uploads || 0;
      const total = data.total_files || files.length;
      
      let message = `Batch upload completed! ${completed}/${total} files uploaded successfully.`;
      if (failed > 0) {
        message += ` ${failed} files failed to upload.`;
      }
      
      // Show success message
      this.$bvToast.toast(message, {
        title: 'Batch Upload Complete',
        variant: failed > 0 ? 'warning' : 'success',
        solid: true,
        autoHideDelay: 5000
      });
      
      if (this.candidateId) {
        // Navigate to the candidate files page
        this.$router.push({ 
          name: 'chatbot-candidate-files',
          params: { candidateId: this.candidateId }
        });
      } else {
        // Fall back to general chatbot page
        this.$router.push({ name: 'chatbot' });
      }
    },

    handleUploadError(err) {
      this.error = 'Error uploading file. Please try again.';
      this.uploadProgress.show = false;
    },

    hideProgressBar() {
      this.uploadProgress.show = false;
    },


  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';

.chatbot-upload-page {
  background: #ffffff;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.upload-container {
  width: 100%;
  height: 100%;
  margin: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  overflow: hidden;
  flex: 1;
}

.upload-header {
  background: #ffffff;
  color: #2d3748;
  padding: 2rem;
  text-align: center;
  position: relative;
  flex-shrink: 0;
  border-bottom: 1px solid #e9ecef;

  .back-btn {
    position: absolute;
    top: 1.5rem;
    left: 1.5rem;
    background: none;
    border: none;
    color: #4a5568;
    padding: 0.5rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }

    i {
      font-size: 16px;
    }
  }

  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    margin: 0 0 1rem 0;
    color: #2d3748;
    font-family: $brand-font-medium;
  }

  p {
    font-size: 1.1rem;
    margin: 0;
    color: #718096;
    font-family: $brand-font-regular;
  }
}

.progress-section {
  padding: 2rem;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;

  .progress-bar {
    width: 100%;
    height: 8px;
    background: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 1rem;

    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, $color-primary 0%, darken($color-primary, 10%) 100%);
      border-radius: 4px;
      transition: width 0.3s ease;
    }
  }

  .progress-text {
    text-align: center;
    color: #6c757d;
    font-size: 14px;
    font-weight: 500;
    
    .progress-status {
      font-weight: 600;
      color: #495057;
      margin-bottom: 0.5rem;
      font-size: 16px;
    }
    
    .progress-percentage {
      font-size: 18px;
      font-weight: 700;
      color: $color-primary;
      margin-bottom: 0.5rem;
    }
    
    .progress-message {
      color: #6c757d;
      margin-bottom: 0.5rem;
      font-style: italic;
    }
    
    .progress-pages {
      color: #868e96;
      font-size: 12px;
    }
  }
}

.error-message {
  background: #f8d7da;
  color: #721c24;
  padding: 1rem;
  margin: 1rem 2rem;
  border-radius: 6px;
  border: 1px solid #f5c6cb;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    font-size: 18px;
  }
}
</style> 